import React, { FC, useMemo, useState } from "react";
import { throwBadKind } from "utils/throwBadType";
import { ColumnIDs } from "../Types";
import style from "./style.scss";

import { useHistory } from "react-router";
import { Button, Link, Tooltip } from "@xapp/components";
import { Dialog, DialogActionProps } from "react-toolbox/lib/dialog";

export interface AppsTableCellSwitchProps {
    readonly columnId: ColumnIDs;
    readonly appId: string;
    readonly appIcon: string;
    readonly appName: string;
    readonly createdDate: string;
    readonly lastModifiedDate: string;
    readonly submitter: string;
    readonly status: string;
    readonly channels: any[];
    readonly organizationId: string;
    readonly notes: string;
    onActionClick?(status: string, appId: string): void;
    showSnippetDialog: () => void;
    showSnippetDialogKey: (key: string) => void;
    handleLive: (disabled: boolean) => void;
    valueNotes?: (note: string, id: string, status: string) => void;
}

export const AppsTableCellSwitch: FC<AppsTableCellSwitchProps> = (props) => {
    const {
        columnId,
        appId,
        appIcon,
        appName,
        createdDate,
        lastModifiedDate,
        submitter,
        status,
        channels,
        organizationId,
        notes,
        onActionClick,
        showSnippetDialog,
        showSnippetDialogKey,
        handleLive,
        valueNotes,
    } = props;
    const history = useHistory();

    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [notesDialog, setNotesDialog] = useState(notes);

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    const handleSaveNotes = () => {
        valueNotes(notesDialog, appId, status);
        setShowConfirmDialog(false);
    };

    const handleConfirmClick = () => {
        setShowConfirmDialog(true);
    };

    const handleSnippetDialog = (key: string) => {
        showSnippetDialog();
        showSnippetDialogKey(key);
    };

    const renderReadyForInstall = () => {
        if (channels?.length === 1) {
            const key = channels[0].key;
            return <Button variation="flat" label="" leftIcon="extension" onClick={() => handleSnippetDialog(key)} />;
        }
        if (channels?.length > 1) {
            return <Button variation="flat" label="channels" onClick={redirectChannels} />;
        }
        return <></>;
    };

    const channelsDisabled: boolean = useMemo(() => {
        let disabled = false;
        channels?.forEach((chnl) => {
            if (chnl.disabled === true) {
                disabled = true;
                return;
            }
        });
        return disabled;
    }, [channels]);

    const redirectChannels = () => {
        const routeBase = `/${organizationId}/${appId}`;
        return history.push(`${routeBase}/channels`);
    };

    const dialogActions: DialogActionProps[] = [
        {
            label: "cancel",
            onClick: handleCancel,
        },
        {
            label: `${channelsDisabled ? "Enable" : "Disable"}`,
            onClick: () => handleLive(channelsDisabled),
            primary: true,
            raised: true,
        },
    ];

    const dialogNotes: DialogActionProps[] = [
        {
            label: "cancel",
            onClick: handleCancel,
        },
        {
            label: "save",
            onClick: handleSaveNotes,
            primary: true,
            raised: true,
        },
    ];

    const renderAction = () => {
        const statusCase = status?.toLowerCase();
        switch (statusCase) {
            case "live":
                return (
                    <div>
                        {channels?.length > 1 && (
                            <Tooltip content={`${channelsDisabled ? "Enable chat widget" : "Disable chat widget"}`}>
                                <Button
                                    className={`${channelsDisabled ? style.buttonOff : style.buttonOn}`}
                                    onClick={handleConfirmClick}
                                    leftIcon="power_settings_new"
                                />
                            </Tooltip>
                        )}{" "}
                        <Dialog
                            actions={dialogActions}
                            title={`${channelsDisabled ? "Enable" : "Disable"} Widget`}
                            active={showConfirmDialog}
                        >
                            <>
                                <p>{`Are you sure you want ${channelsDisabled ? "enable" : "disable"} this app?`}</p>
                            </>
                        </Dialog>
                    </div>
                );
            case "ready for install":
                return <div>{renderReadyForInstall()}</div>;
            case "under review":
                return (
                    <div>
                        <Button
                            variation="flat"
                            label="Ready"
                            onClick={() => onActionClick("Under Review", appId)}
                        />
                    </div>
                );
            case "submitted for review":
                return (
                    <div>
                        <Button
                            variation="flat"
                            label="Review"
                            onClick={() => onActionClick("Submitted For Review", appId)}
                        />
                    </div>
                );
            default:
        }
    };

    const renderNotes = () => {
        return (
            <div>
                {notesDialog?.length > 0 ? (
                    <Tooltip content={notesDialog?.length > 140 ? `${notesDialog?.slice(0, 139)}...` : notesDialog}>
                        <Button variation="flat" leftIcon="description" onClick={handleConfirmClick} />
                    </Tooltip>
                ) : (
                    <Button variation="flat" leftIcon="description" onClick={handleConfirmClick} />
                )}

                <Dialog actions={dialogNotes} title={`Add note for ${appName}`} active={showConfirmDialog}>
                    <textarea
                        style={{ width: "100%" }}
                        value={notesDialog}
                        onChange={(evt) => setNotesDialog(evt.target.value)}
                    />
                </Dialog>
            </div>
        );
    };

    switch (columnId) {
        case "app_name":
            return (
                <div className={style.appNameWrapper}>
                    <img src={appIcon} alt="App Icon" className={style.appIcon} />
                    <Link href={`/${organizationId}/${appId}`}>
                        <div className={style.appTableRowText}>{appName}</div>
                    </Link>
                </div>
            );
        case "created_date":
            return <div className={style.appTableRowText}>{createdDate}</div>;
        case "last_modified":
            return <div className={style.appTableRowText}>{lastModifiedDate}</div>;
        case "submitter":
            return <div className={style.appTableRowText}>{submitter}</div>;
        case "action":
            return <div>{renderAction()}</div>;
        case "notes":
            return <div>{renderNotes()}</div>;
        default:
            throw throwBadKind(columnId);
    }
};
